import axios, { AxiosInstance, AxiosHeaders } from 'axios';
import { setCookie, getCookie, deleteCookie } from '../utils';
import { isHttpUrl, getCleanUrlPath } from '../helpers';
import {
	CsrfConfig,
	IApiResponse,
	IRequestConfig,
	IRequestInterceptorConfig,
	TResponseInterceptorConfig,
} from '../types';

export class CsrfService {
	private _axiosInstance: AxiosInstance;
	constructor(private _config: CsrfConfig) {
		const { baseURL, timeout, headers } = this._config;
		this._axiosInstance = axios.create({
			withCredentials: true,
			baseURL,
			timeout,
			headers,
		});
	}

	private _getCSRFCookieValue = () => {
		return getCookie(this._config.csrfCookieKey);
	};

	private _setCSRFCookie = (token?: string) => {
		/**
		 * In case if we get the CSRF value in the response header then we are setting the CSRF value in cookie to keep
		 * the cookie value in sync for multiple tabs. Otherwise, we will remove the cookie value if we are not getting it
		 * in the header.
		 */
		if (token) {
			setCookie(this._config.csrfCookieKey, token, {
				expires: {
					days: 7,
				},
				domain: '.phonepe.com',
			});
		} else {
			deleteCookie(this._config.csrfCookieKey, {
				domain: '.phonepe.com',
			});
		}
	};

	setCSRFToken = (headers?: IApiResponse['headers']) => {
		/**
		 * If the request method is other then the GET request we are setting up the CSRF cookie from the header of the response.
		 */
		if (headers?.[this._config.csrfKey]) {
			this._setCSRFCookie(headers[this._config.csrfKey]);
		}
	};

	removeCSRFToken = () => {
		this._setCSRFCookie();
	};

	private _getToken = (req: IRequestConfig) => {
		const fullUrl = isHttpUrl(req.url!)
			? req.url!
			: getCleanUrlPath(req.baseURL ?? '') + req.url;

		if (req.method === 'get') {
			return Promise.resolve();
		}
		if (req.method === 'post') {
			return this._axiosInstance.post(fullUrl);
		}
		if (req.method === 'patch') {
			return this._axiosInstance.patch(fullUrl);
		}
		if (req.method === 'delete') {
			return this._axiosInstance.delete(fullUrl);
		}
		if (req.method === 'put') {
			return this._axiosInstance.put(fullUrl);
		}
	};

	private _setToken = (request: IRequestInterceptorConfig, token: string) => {
		this._setCSRFCookie(token);
		if (!request.headers) {
			request.headers = new AxiosHeaders();
		}
		request.headers[this._config.csrfKey] = token;
	};

	addCSRFtoReq = (request: IRequestInterceptorConfig) => {
		const cookieValue = this._getCSRFCookieValue();
		/**
		 * We are not adding the CSRF header in case of the request type is of GET request because in the backend
		 * the logic for CSRF validation is not added on the GET request so we are also not sending the CSRF token
		 * in the GET request headers.
		 */
		if (request && cookieValue && request.method !== 'get') {
			request.headers![this._config.csrfKey] = cookieValue;
			return request;
		}
		return this._getToken(request)!
			.then((res) => {
				if (res && res.headers)
					this._setToken(request, res.headers[this._config.csrfKey]);
				return request;
			})
			.catch((error) => {
				if (error.code === 'ECONNABORTED') {
					return Promise.reject(error);
				}
				if (error && error.response && error.response.headers)
					this._setToken(request, error.response.headers[this._config.csrfKey]);
				return request;
			});
	};

	checkCSRFinRes = (response: TResponseInterceptorConfig) => {
		this.setCSRFToken(response.headers);
		return response;
	};
}
